import type {ColumnFragment, ValueFragment, ValueInstanceFragment} from "app/gql/graphqlSchema";
import {IonButton, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonIcon, IonLabel} from "@ionic/react";
import {isNil, isNotBlank} from "app/utils/stdlib";
import {useControlCardInstanceContext} from "app/employee/controlCard/ControlCardInstanceContext";
import {IonCardFooter} from "app/employee/controlCard/ControlCardUI";
import {checkmarkCircleOutline, closeOutline, refreshCircleOutline, timeOutline} from "ionicons/icons";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {EMPTY_VALUE} from "app/employee/controlCard/types";
import {useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {RhfFstIonDatetimeButton} from "app/employee/rhf/RhfFstIonDatetimeButton";
import {zLocalTime, zToNull} from "app/utils/validator";
import {FlexColumnCenter} from "app/employee/flexUtils";
import type {Color} from "@ionic/core";
import {timeLuxonToApi} from "app/utils/dates";
import {DateTime} from "luxon";
import {EditContainer} from "app/employee/incdoc/EditContainer";
import {SavingProgress} from "app/employee/controlCard/input/SavingProgress";
import {useExpressionValue} from "app/employee/controlCard/hook/useExpressionValue";
import {transformToApplicationError} from "app/gql/handleGraphqlErrors";
import {setApplicationError} from "app/utils/parseErrorResult";
import {DisplayRootError} from "app/employee/controlCard/input/DisplayRootError";
import {DisplayValueInstanceError} from "app/employee/controlCard/input/DisplayValueInstanceError";
import {useHasControlCardValueError} from "app/employee/controlCard/hook/useHasControlCardValueError";
import {InputIonCard} from "app/employee/controlCard/input/InputIonCard";

interface Props {
  column: ColumnFragment & {__typename: "ControlCardTimeColumn"};
  valueInstance: ValueInstanceFragment;
  disabled?: boolean;
}

const formSchema = z.object({
  value: zToNull.or(zLocalTime)
});

export type FormData = z.infer<typeof formSchema>;

export const InputTime = ({column, valueInstance, disabled}: Props) => {
  const {t} = useTranslation();
  const {isComplete, canEdit} = useControlCardInstanceContext();
  const hasError = useHasControlCardValueError(valueInstance);

  const {persistedValue, convertToComputed, showRestoreExpression, saving, saveValue} = useExpressionValue(
    column,
    valueInstance
  );

  let value: string | null = null;
  if (persistedValue.__typename === "ControlCardTimeValue") {
    value = persistedValue.time;
  }

  const {
    control,
    formState: {isDirty, isSubmitting, disabled: formDisabled, errors},
    handleSubmit,
    reset,
    watch,
    setValue,
    setError
  } = useForm<FormData>({
    values: {
      value
    },
    disabled: isComplete || !!disabled || saving || !canEdit,
    resolver: zodResolver(formSchema)
  });

  const handleSave = useMemo(
    () =>
      handleSubmit(async (data) => {
        try {
          let value: ValueFragment;
          if (isNil(data.value)) {
            value = EMPTY_VALUE;
          } else {
            value = {__typename: "ControlCardTimeValue", time: data.value};
          }

          await saveValue(value);
        } catch (e) {
          const appError = transformToApplicationError(e);
          setApplicationError(appError, setError);
        }
      }),
    [handleSubmit, saveValue, setError]
  );

  const handleClear = () => reset();

  useEffect(() => {
    if (isComplete) {
      reset();
    }
  }, [isComplete, reset]);

  const watchedValue = watch("value");
  const color: Color = isNil(watchedValue) ? "warning" : "primary";

  const setNow = async () => {
    try {
      setValue("value", timeLuxonToApi(DateTime.now()), {shouldDirty: true});
      await handleSave();
    } catch (e) {
      const appError = transformToApplicationError(e);
      setApplicationError(appError, setError);
    }
  };

  useEffect(() => {
    const subscription = watch((_, {name, type}) => {
      if (name === "value" && type === "change") {
        handleSave();
      }
    });
    return () => subscription.unsubscribe();
  }, [handleSave, watch]);

  return (
    <InputIonCard $hasError={hasError} data-scroll-to-value-instance-id={valueInstance.id}>
      <EditContainer>
        <IonButton disabled={formDisabled} fill="clear" onClick={setNow}>
          <IonIcon icon={timeOutline} slot="icon-only" />
        </IonButton>
        {showRestoreExpression && (
          <IonButton disabled={formDisabled} fill="clear" onClick={convertToComputed}>
            <IonIcon icon={refreshCircleOutline} slot="icon-only" />
          </IonButton>
        )}
      </EditContainer>
      <IonCardHeader>
        <IonCardTitle>{column.title}</IonCardTitle>
        {isNotBlank(column.description) && <IonCardSubtitle>{column.description}</IonCardSubtitle>}
      </IonCardHeader>
      <IonCardContent>
        <DisplayRootError errors={errors} />
        <FlexColumnCenter>
          <RhfFstIonDatetimeButton
            buttonColor={color}
            buttonSize="large"
            initialValue={column.initialValue}
            presentation="time"
            rhf={{control, name: "value"}}
          >
            <IonLabel slot="title">{column.title}</IonLabel>
          </RhfFstIonDatetimeButton>
          <DisplayValueInstanceError column={column} disabled={formDisabled} valueInstance={valueInstance} />
        </FlexColumnCenter>
      </IonCardContent>
      {isDirty && !isSubmitting && (
        <IonCardFooter>
          <IonButton color="primary" fill="outline" onClick={handleClear} size="small">
            <IonIcon icon={closeOutline} slot="start" />
            {t("g.cancel")}
          </IonButton>
          <IonButton color="primary" disabled={formDisabled} onClick={handleSave} size="small">
            <IonIcon icon={checkmarkCircleOutline} slot="start" />
            {t("g.save")}
          </IonButton>
        </IonCardFooter>
      )}
      <SavingProgress isSubmitting={saving} />
    </InputIonCard>
  );
};
